import React from "react";

import { displayDoctorNetworkInfo } from "QuotePage/NetworkSizeConstants";

import { deductibleOptions, networkSizeMock, carrierOptions, doctorVisitOptionsMock, specialistVisitOptionsMock, networkOptions } from "../shared/FilterData";
import MultiSelectFilter from "../shared/MultiSelectFilter";
import SingleOptionFilter from "../shared/SingleOptionFilter";
import { DropdownButton } from "Cobalt/Dropdown";

import DropdownMultiMenuFilter from "./plansFilterPanelDumb/DropdownMultiMenuFilter";
import DropdownMultiSelectFilter from "./plansFilterPanelDumb/DropdownMultiSelectFilter";

import type { MedicalPlan } from "Utils/types";

type PrescriptionFilters = {
  genericDrugCopays: boolean;
  brandDrugCopays: boolean;
  drugsSubjectToDeductible: boolean;
};

type OtherFilters = {
  outOfState: boolean;
  hsaEligible: boolean;
  maxPlanCost: boolean;
};

type Filters = {
  deductible: boolean;
  network: boolean;
  carrier: boolean;
  doctorVisit: boolean;
  specialistVisit: boolean;
  overflowPlans: boolean;
  prescriptionFilters: PrescriptionFilters;
  otherFilters: OtherFilters;
};

type Props = {
  allPlans: Array<MedicalPlan>;
  filterConfig: any;
  onChangeFilter: (arg0: string, arg1: any) => void;
  companyProfile: any;
  carriersInRegion: Array<string>;
  filters: Filters;
};

class PlansFilterPanelDumb extends React.Component<Props> {
  static defaultProps = {
    filters: {
      deductible: true,
      network: true,
      carrier: true,
      doctorVisit: true,
      specialistVisit: true,
      overflowPlans: false,
      prescriptionFilters: {
        genericDrugCopays: true,
        brandDrugCopays: true,
        drugsSubjectToDeductible: true,
      },

      otherFilters: {
        outOfState: true,
        hsaEligible: true,
        maxPlanCost: false,
      },
    },
  };

  render() {
    const { filters } = this.props;
    return (
      <div className="flex flex-row scroll-auto gap-3 justify-end items-center">
        {filters.deductible && this.renderStandaloneDeductibleFilter()}
        {filters.network && this.renderNetworkFilter()}
        {filters.carrier && this.renderStandaloneCarrierFilter()}
        {filters.doctorVisit && this.renderStandaloneDoctorVisitFilter()}
        {filters.specialistVisit && this.renderStandaloneSpecialistVisitFilter()}
        {(filters.prescriptionFilters || filters.otherFilters) && this.renderMoreFiltersMenu()}
      </div>
    );
  }

  renderStandaloneDeductibleFilter() {
    const { filterConfig, onChangeFilter } = this.props;
    const options = deductibleOptions;
    return (
      <DropdownMultiSelectFilter
        filterConfig={filterConfig}
        onChangeFilter={onChangeFilter}
        options={options}
        defaultOptions={options.map((x) => x.value)}
        defaultSelect
        filterKey="deductible"
        menuTitle="Deductible"
      />
    );
  }

  renderStandaloneCarrierFilter() {
    const { filterConfig, onChangeFilter, carriersInRegion } = this.props;
    const options = carrierOptions(carriersInRegion.slice(0).sort());
    return (
      <DropdownMultiSelectFilter
        filterConfig={filterConfig}
        onChangeFilter={onChangeFilter}
        options={options}
        defaultOptions={carriersInRegion}
        defaultSelect
        filterKey="carrier"
        menuTitle="Carriers"
      />
    );
  }

  renderNetworkFilter() {
    const { filterConfig, onChangeFilter } = this.props;
    const planState = this.props.companyProfile && this.props.companyProfile.state;
    const filters = [
      {
        options: networkOptions(this.props.allPlans),
        filterKey: "plan_type",
        menuTitle: "Network Type",
        defaultSelect: true,
      },
    ];

    if (displayDoctorNetworkInfo(planState)) {
      filters.push({
        options: networkSizeMock(),
        filterKey: "network_size",
        menuTitle: "Network Size",
        defaultSelect: true,
      });
    }
    return <DropdownMultiMenuFilter filterConfig={filterConfig} onChangeFilter={onChangeFilter} menus={filters} menuTitle="Networks" />;
  }

  renderStandaloneDoctorVisitFilter() {
    const { filterConfig, onChangeFilter } = this.props;
    const options = doctorVisitOptionsMock;

    return (
      <DropdownMultiSelectFilter
        filterConfig={filterConfig}
        onChangeFilter={onChangeFilter}
        options={options}
        defaultOptions={options.map((x) => x.value)}
        defaultSelect
        filterKey="estimated_costs.pcp_visit"
        menuTitle="Doctor Visits"
        className="max-sm:hidden"
      />
    );
  }

  renderStandaloneSpecialistVisitFilter() {
    const { filterConfig, onChangeFilter } = this.props;
    const options = specialistVisitOptionsMock;

    return (
      <DropdownMultiSelectFilter
        filterConfig={filterConfig}
        onChangeFilter={onChangeFilter}
        options={options}
        defaultOptions={options.map((x) => x.value)}
        defaultSelect
        filterKey="estimated_costs.specialist_visit"
        menuTitle="Specialist Visits"
        className="max-md:hidden"
      />
    );
  }

  renderOverflowPlansFilter() {
    const { filterConfig, onChangeFilter } = this.props;
    const options = [
      {
        label: "On Market Plans",
        value: false,
      },

      {
        label: "Hidden plans",
        value: true,
      },
    ];

    return (
      <DropdownMultiSelectFilter
        filterConfig={filterConfig}
        onChangeFilter={onChangeFilter}
        options={options}
        defaultOptions={[false]}
        defaultSelect
        filterKey="overflow_plan"
        menuTitle="Hidden Plans"
      />
    );
  }

  renderMoreFiltersMenu() {
    const { filters } = this.props;
    const { otherFilters, prescriptionFilters, overflowPlans } = filters;

    return (
      <DropdownButton
        title="More Filters"
        color="ghost"
        className="block text-sm"
        type="filter"
        dropdownClassName="text-sm flex flex-col gap-2 rounded-xl"
        keepOpenOnSelect
        dropdownPosition="right"
      >
        {filters.specialistVisit && (
          <ul className="p-0 list-none md:hidden">
            <span className="font-semibold mb-1">Specialist Visits</span>
            {this.renderSpecialistVisitFilter()}
          </ul>
        )}

        {prescriptionFilters && (
          <ul className="p-0 list-none">
            <span className="font-semibold mb-1">Prescriptions</span>
            {this.renderDrugsFilter()}
          </ul>
        )}

        {otherFilters && (
          <ul className="p-0 list-none">
            <span className="font-semibold mb-1">Other Filters</span>
            {otherFilters.outOfState && this.renderOutOfStateFilter()}
            {otherFilters.hsaEligible && this.renderHSAFilter()}
          </ul>
        )}

        {overflowPlans && this.renderOverflowPlansFilter()}
      </DropdownButton>
    );
  }

  renderDrugsFilter() {
    const { allPlans, filterConfig, onChangeFilter, filters } = this.props;
    const { prescriptionFilters } = filters;
    return (
      <>
        {prescriptionFilters.genericDrugCopays && (
          <SingleOptionFilter
            filterConfig={filterConfig}
            allPlans={allPlans}
            filterKey="generic_drug_copays"
            handleChangeFilter={onChangeFilter}
            checkedFilter={{ exclude: [false] }}
            uncheckedFilter={{}}
            filterLabel="Generic Drug Copays"
            checked={filterConfig.generic_drug_copays ? filterConfig.generic_drug_copays.exclude : false}
            filterTooltip="These plans have copays on generic drugs so you pay lower costs on generic drugs right away without having to worry about a deductible."
            disableFilterCountIndicator
            underline
            tooltipPlacement="left"
          />
        )}

        {prescriptionFilters.brandDrugCopays && (
          <SingleOptionFilter
            filterConfig={filterConfig}
            allPlans={allPlans}
            filterKey="brand_drug_copays"
            handleChangeFilter={onChangeFilter}
            checkedFilter={{ exclude: [false] }}
            uncheckedFilter={{}}
            filterLabel="Brand Drug Copays"
            checked={filterConfig.brand_drug_copays ? filterConfig.brand_drug_copays.exclude : false}
            filterTooltip="These plans have copays on brand drugs so you pay lower costs on brand drugs right away without having to worry about a deductible."
            disableFilterCountIndicator
            underline
            tooltipPlacement="left"
          />
        )}

        {prescriptionFilters.drugsSubjectToDeductible && (
          <SingleOptionFilter
            filterConfig={filterConfig}
            allPlans={allPlans}
            filterKey="all_drugs_subject_to_deductible"
            handleChangeFilter={onChangeFilter}
            checkedFilter={{ exclude: [false] }}
            uncheckedFilter={{}}
            filterLabel="Drugs Subject to Deductible"
            checked={filterConfig.all_drugs_subject_to_deductible ? filterConfig.all_drugs_subject_to_deductible.exclude : false}
            filterTooltip={"These plans have drugs subject to a deductible so you'll pay full price on drugs until you meet the deductible."}
            disableFilterCountIndicator
            underline
            tooltipPlacement="left"
          />
        )}
      </>
    );
  }

  renderSpecialistVisitFilter() {
    const { allPlans, filterConfig, onChangeFilter } = this.props;
    const options = specialistVisitOptionsMock;

    // NOTE: in prod, this is not working as expected
    // clicking an option may in many cases toggle the other
    // This will need to be addressed in a separate PR
    return (
      <MultiSelectFilter
        allPlans={allPlans}
        filterConfig={filterConfig}
        onChangeFilter={onChangeFilter}
        options={options}
        filterKey="estimated_costs.specialist_visit"
        menuTitle="Specialist Visits"
        disableFilterCountIndicator
        defaultOptions={options.map((option) => option.value)}
      />
    );
  }

  renderOutOfStateFilter() {
    const { allPlans, filterConfig, onChangeFilter, companyProfile } = this.props;
    return (
      <SingleOptionFilter
        filterConfig={filterConfig}
        allPlans={allPlans}
        filterKey="out_of_state"
        handleChangeFilter={onChangeFilter}
        filterLabel="Out of State Employee Coverage"
        filterTooltip={`Certain plans do not cover employees living <b>outside of ${companyProfile.state_name}</b>.`}
        disableFilterCountIndicator
        underline
        tooltipPlacement="left"
      />
    );
  }

  renderHSAFilter() {
    const { allPlans, filterConfig, onChangeFilter } = this.props;
    return (
      <SingleOptionFilter
        filterConfig={filterConfig}
        allPlans={allPlans}
        filterKey="hsa_eligible"
        handleChangeFilter={onChangeFilter}
        filterLabel="Health Savings Account (HSA) Eligible"
        filterTooltip="HSAs allow you to use pre-tax money to pay for medical expenses. If you have significant medical expenses, an HSA plan can help save up to 30% of the cost."
        disableFilterCountIndicator
        underline
        tooltipPlacement="left"
      />
    );
  }
}

export default PlansFilterPanelDumb;
